import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  Calendar,
  Col,
  DatePicker,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
} from 'antd';
import { PageProps } from '@/types/PageProps';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import moment, { Moment } from 'moment';
import { useCallback, useState } from 'react';
import StatusTag from '@/components/_common/StatusTag';
import { useWindowSize } from '@/hooks';
import {
  workorderComplementColor,
  workorderComplementLabel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { DateTimeFormat } from '@/utils/moment';
import { FloatingFormItem, Group } from '@/components/_common';
import WorkorderCalendarModal from '@/components/Movements/WorkOrders/Calendar/WorkorderCalendarModal';
import { workorderRoutes } from '../route';
import { useWorkOrderTrackingCalendar } from '@/core/domain/workorder/Tracking/Tracking.hook';
import {
  WorkOrderTrackingCalendar,
  WorkOrderTrackingCalendarDateType,
} from '@/core/domain/workorder/Tracking/Tracking.types';
import AgendaList, { AgendaData } from '@/components/_common/AgendaList';

import './style.less';
import {
  ComplementOriginEnum,
  ComplementOriginTranslator,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { isAppPortocar } from '@/index';
import Agenda from '@/components/Agenda';

const options = [
  {
    value: WorkOrderTrackingCalendarDateType.Authorization,
    label: 'Data de autorização',
    color: '#c6b822',
  },
  {
    value: WorkOrderTrackingCalendarDateType.Close,
    label: 'Data de fechamento',
    color: '#27aa57',
  },
  {
    value: WorkOrderTrackingCalendarDateType.Entry,
    label: 'Data de entrada',
    color: '#348adb',
  },
  {
    value: WorkOrderTrackingCalendarDateType.Estimated,
    label: 'Data de saída prevista',
    color: '#27b1c3',
  },
  {
    value: WorkOrderTrackingCalendarDateType.Return,
    label: 'Data de entrega',
    color: '#348adb',
  },
];

const dateKey: Record<
  WorkOrderTrackingCalendarDateType,
  keyof WorkOrderTrackingCalendar
> = {
  [WorkOrderTrackingCalendarDateType.Authorization]: 'authorizationDate',
  [WorkOrderTrackingCalendarDateType.Close]: 'closingDate',
  [WorkOrderTrackingCalendarDateType.Entry]: 'entryDate',
  [WorkOrderTrackingCalendarDateType.Estimated]: 'estimatedLeaveDate',
  [WorkOrderTrackingCalendarDateType.Return]: 'returnDate',
};

function WorkOrderCalendarPage({ route }: PageProps) {
  const { isMobile } = useWindowSize();
  const intl = useIntl();
  const [date, setDate] = useState<Moment>(moment());
  const [complementOrigin, setComplementOrigin] =
    useState<ComplementOriginEnum>();
  const [dateType, setDateType] = useState<WorkOrderTrackingCalendarDateType>(
    WorkOrderTrackingCalendarDateType.Authorization,
  );
  const [workorderSelected, setWorkorderSelected] =
    useState<WorkOrderTrackingCalendar>();

  const workorders = useWorkOrderTrackingCalendar({
    startDate: date.clone().startOf('month').format(DateTimeFormat),
    endDate: date.clone().endOf('month').format(DateTimeFormat),
    complementOrigin,
    dateType,
  });

  const dateCellRender = useCallback(
    (value: Moment) => {
      if (workorders.isLoading) {
        return (
          <Space wrap>
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
          </Space>
        );
      }

      if (value.isBefore(date, 'month') || value.isAfter(date, 'month')) {
        return null;
      }

      return (
        <Space wrap title={value.format('LL')}>
          {!!workorders.data &&
            workorders.data
              ?.filter((item) =>
                value.isSame(moment(item[dateKey[dateType]] as string), 'day'),
              )
              .map((item) => (
                <StatusTag
                  key={item.workOrderId}
                  label={`${item.complementIdentifier} | ${intl.formatMessage({
                    id: `workOrders.${item.status}`,
                  })}`}
                  color={workorderComplementColor[item.status]}
                  title={`${
                    options.find((option) => option.value === dateType)?.label
                  }: ${moment(item[dateKey[dateType]] as string).format(
                    'LLL',
                  )}`}
                  onClick={() => setWorkorderSelected(item)}
                />
              ))}
        </Space>
      );
    },
    [workorders.dataUpdatedAt],
  );

  const newRoute = {
    ...route.parent,
    routes: [
      route.parent,
      ...route.parent.routes.filter((route) =>
        workorderRoutes.includes(route.key),
      ),
    ],
  };

  function agendaTag(item: WorkOrderTrackingCalendar) {
    return (
      <StatusTag
        key={item.workOrderId}
        label={`${item.complementIdentifier} | ${intl.formatMessage({
          id: `workOrders.${item.status}`,
        })}`}
        color={workorderComplementColor[item.status]}
        onClick={() => setWorkorderSelected(item)}
        style={{ width: '100%' }}
        size="lg"
      />
    );
  }

  const getAgendaData = useCallback(() => {
    return (
      workorders.data?.reduce<AgendaData[]>((acc, item) => {
        const isSameDay = (date: Moment) =>
          moment(item[dateKey[dateType]] as string).isSame(date, 'day');

        if (acc.find((accItem) => isSameDay(accItem.date))) {
          return acc.map((accItem) => {
            if (isSameDay(accItem.date)) {
              return {
                ...accItem,
                render: [...accItem.render, agendaTag(item)],
              };
            }
            return accItem;
          });
        }

        return [
          ...acc,
          {
            date: moment(item[dateKey[dateType]] as string),
            render: [agendaTag(item)],
          },
        ];
      }, [] as AgendaData[]) || []
    );
  }, [workorders.dataUpdatedAt]);

  function renderFilterDateType(agenda: boolean) {
    return (
      <FloatingFormItem label="Filtrar por">
        <Select
          placeholder="Selecionar tipo da OS"
          style={agenda ? { marginBottom: 12, width: '100%' } : { width: 230 }}
          value={dateType}
          onChange={setDateType}
          options={options}
        />
      </FloatingFormItem>
    );
  }

  const originBlocked = isAppPortocar
    ? new Set([ComplementOriginEnum.MyBoschCarService])
    : new Set([
        ComplementOriginEnum.PortoBattery,
        ComplementOriginEnum.PortoSchedule,
      ]);

  const complementOriginArray = Object.values(ComplementOriginEnum)
    .filter((origin) => !originBlocked.has(origin))
    .map((value) => {
      const origins = Object.values(ComplementOriginEnum);

      return {
        value: origins.findIndex((origin) => origin === value),
        label: ComplementOriginTranslator[value],
      };
    });

  const [viewType, setViewType] = useState<'calendar' | 'agenda'>('calendar');

  return (
    <>
      <DefaultRouteTabs route={newRoute} permissions={route.permissions}>
        <Helmet title={intl.formatMessage({ id: route.name })} />

        <Segmented
          value={viewType}
          options={[
            { value: 'calendar', label: 'Calendário' },
            { value: 'agenda', label: 'Agenda' },
          ]}
          onChange={(value) => setViewType(value as typeof viewType)}
          style={{ marginBottom: 24 }}
        />

        {viewType === 'agenda' && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col>{renderFilterDateType(true)}</Col>
              <Col>
                <FloatingFormItem label="Origem do orçamento">
                  <Select
                    allowClear
                    mode="multiple"
                    maxTagTextLength={30}
                    style={{ width: 230 }}
                    maxTagCount="responsive"
                    options={complementOriginArray}
                    value={complementOrigin}
                    onChange={(value) => setComplementOrigin(value)}
                  />
                </FloatingFormItem>
              </Col>
            </Row>

            <Agenda
              onChangeDate={setDate}
              date={date}
              dataSource={
                workorders.data?.map((workOrder) => ({
                  id: workOrder.complementId.toString(),
                  color: workorderComplementColor[workOrder.status],
                  title: workOrder.complementIdentifier,
                  label: workorderComplementLabel[workOrder.status],
                  content: `${moment(workOrder[dateKey[dateType]]).format(
                    'HH:mm',
                  )} • ${
                    workOrder.complementOrigin
                      ? ComplementOriginTranslator[workOrder.complementOrigin]
                      : `Sem origem`
                  }`,
                  day: moment(workOrder[dateKey[dateType]]).format(
                    'YYYY-MM-DD',
                  ),
                  time: moment(workOrder[dateKey[dateType]]).format('HH:mm'),
                  onClick: () => setWorkorderSelected(workOrder),
                })) || []
              }
            />
          </>
        )}

        {viewType === 'calendar' && (
          <>
            {isMobile && (
              <>
                {renderFilterDateType(true)}

                <AgendaList
                  dataSource={getAgendaData()}
                  onChange={setDate}
                  date={date}
                />
              </>
            )}

            {!isMobile && (
              <Calendar
                value={date}
                dateCellRender={dateCellRender}
                mode="month"
                onChange={setDate}
                className="scroll"
                key={workorders.dataUpdatedAt}
                headerRender={(config) => (
                  <Group gap={24} style={{ marginBottom: 24 }}>
                    {renderFilterDateType(false)}

                    <FloatingFormItem label="Mês">
                      <DatePicker.MonthPicker
                        value={config.value}
                        onChange={(inputValue) =>
                          config.onChange(inputValue || moment())
                        }
                        format="MMMM, YYYY"
                        className="input-capitalize"
                        allowClear={false}
                      />
                    </FloatingFormItem>

                    <FloatingFormItem label="Origem do orçamento">
                      <Select
                        allowClear
                        mode="multiple"
                        maxTagTextLength={30}
                        style={{ width: 230 }}
                        maxTagCount="responsive"
                        options={complementOriginArray}
                        value={complementOrigin}
                        onChange={(value) => setComplementOrigin(value)}
                      />
                    </FloatingFormItem>
                  </Group>
                )}
              />
            )}
          </>
        )}
      </DefaultRouteTabs>

      <WorkorderCalendarModal
        open={!!workorderSelected}
        onCancel={() => setWorkorderSelected(undefined)}
        workorder={workorderSelected}
      />
    </>
  );
}

export default WorkOrderCalendarPage;
